import { createContext, useContext, useState, useCallback, useEffect } from "react";

/**
 * Context for managing unsaved changes across the application.
 * Components register their dirty state, and navigation is blocked globally.
 */
const UnsavedChangesContext = createContext(null);

export function UnsavedChangesProvider({ children }) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  // Register unsaved changes from a component
  const setUnsavedChanges = useCallback((isDirty) => {
    setHasUnsavedChanges(isDirty);
  }, []);

  // Called when user tries to navigate
  const requestNavigation = useCallback((navigateFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigateFn);
      setShowDialog(true);
      return false; // Navigation blocked
    }
    return true; // Navigation allowed
  }, [hasUnsavedChanges]);

  // User confirms they want to leave
  const confirmNavigation = useCallback(() => {
    setShowDialog(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);

  // User cancels navigation
  const cancelNavigation = useCallback(() => {
    setShowDialog(false);
    setPendingNavigation(null);
  }, []);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "יש שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const value = {
    hasUnsavedChanges,
    setUnsavedChanges,
    requestNavigation,
    confirmNavigation,
    cancelNavigation,
    showDialog,
  };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

/**
 * Hook to access the unsaved changes context
 */
export function useUnsavedChangesContext() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error("useUnsavedChangesContext must be used within UnsavedChangesProvider");
  }
  return context;
}

/**
 * Hook for components to register their dirty state.
 * Automatically cleans up when component unmounts.
 * 
 * @param {boolean} isDirty - Whether the form has unsaved changes
 * 
 * @example
 * // In your form component:
 * const isDirty = useMemo(() => JSON.stringify(formData) !== JSON.stringify(initialData), [formData]);
 * useRegisterUnsavedChanges(isDirty);
 */
export function useRegisterUnsavedChanges(isDirty) {
  const { setUnsavedChanges } = useUnsavedChangesContext();

  useEffect(() => {
    setUnsavedChanges(isDirty);
    
    // Cleanup - unregister when component unmounts
    return () => {
      setUnsavedChanges(false);
    };
  }, [isDirty, setUnsavedChanges]);
}

export default UnsavedChangesContext;
